import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core'
import { Field } from '@angular/forms/signals'
import { MatIconButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { kannErhoehen, kannVerringern, NumberStepperZustand, zeigtEntfernen } from './number-stepper.model'

@Component({
  selector: 'lib-number-stepper',
  imports: [MatIconButton, MatIcon],
  templateUrl: './number-stepper.component.html',
  styleUrl: './number-stepper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumberStepperComponent {
  readonly field = input.required<Field<number>>()
  readonly min = input<number>(1)
  readonly max = input<number | undefined>(undefined)
  readonly step = input<number>(1)
  readonly ariaLabel = input<string | undefined>(undefined)
  /**
   * Optionale Einheit hinter der Zahl („mm", „St."). Sie steht IM Feld und nicht daneben, weil
   * die Zahl allein bei Massangaben nichts aussagt — ohne sie liest man „0" und muss die Einheit
   * aus der Beschriftung darueber mitnehmen.
   */
  readonly einheit = input<string>('')
  readonly removable = input<boolean>(false)

  readonly remove = output<void>()

  readonly value = computed(() => this.field()().value() ?? 0)
  /** Nur der WERT ist gesperrt. Das Entfernen der Zeile haengt nicht daran — Begruendung in {@link zeigtEntfernen}. */
  readonly disabled = computed(() => this.field()().disabled() || this.field()().readonly())

  private readonly zustand = computed<NumberStepperZustand>(() => ({
    removable: this.removable(),
    gesperrt: this.disabled(),
    nurLesen: this.field()().readonly(),
    wert: this.value(),
    min: this.min(),
    max: this.max(),
  }))

  readonly showRemove = computed(() => zeigtEntfernen(this.zustand()))
  readonly canDecrement = computed(() => kannVerringern(this.zustand()))
  readonly canIncrement = computed(() => kannErhoehen(this.zustand()))

  decrement() {
    if (this.showRemove()) {
      this.remove.emit()
      return
    }
    if (!this.canDecrement()) return
    const next = Math.max(this.min(), this.value() - this.step())
    this.field()().value.set(next)
    this.field()().markAsDirty()
  }

  increment() {
    if (!this.canIncrement()) return
    const max = this.max()
    const candidate = this.value() + this.step()
    const next = max === undefined ? candidate : Math.min(max, candidate)
    this.field()().value.set(next)
    this.field()().markAsDirty()
  }
}
